import React, { useState, useEffect } from "react";
import { Routes, Route, useNavigate } from "react-router-dom";
import "./Dimenssions.css";
import axiosInstance from "../../../Config/axiosInstance";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { DataGrid } from "@mui/x-data-grid";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Typography from "@mui/material/Typography";

import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";

import AddDimenssions from "./AddDimenssions";
import EditDimenssions from "./EditDimenssions";
import NotAuthorized from "../../../Components/ProtectedRoute/NotAuthorized";

const getPathFromUrl = (url) => {
  try {
    const { pathname } = new URL(url);
    return pathname;
  } catch (e) {
    console.error("Eroare la extragerea URL-ului:", e);
    return url;
  }
};

const DimenssionsList = ({ canCreate, canEdit, canDelete }) => {
  const [rows, setRows] = useState([]);
  const navigate = useNavigate();
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedDimenssionId, setSelectedDimenssionsId] = useState(null);
  const [errorDialogOpen, setErrorDialogOpen] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const fetchDimenssions = async () => {
      try {
        const response = await axiosInstance.get("/dimensions/");
        //sorteaza descrescator dupa ID (cel mai recent primul)
        const sortedData = response.data.sort((a, b) => b.id - a.id);

        //campul nr pentru fiecare rand
        const dataWithNr = sortedData.map((item, index) => ({
          ...item, 
          nr: sortedData.length - index
        }));

        setRows(dataWithNr);
      } catch (error) {
        console.error("Eroarea preluării dimensiunilor:", error);
      }
    };
    fetchDimenssions();
  }, []);

  const handleOpenDialog = (id) => {
    setSelectedDimenssionsId(id);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedDimenssionsId(null);
  };

  const handleConfirmDelete = async () => {
    try {
      await axiosInstance.delete(`/dimensions/${selectedDimenssionId}/`);
      setRows((prevRows) => {
        //elimina randul sters
        const filteredRows = prevRows.filter((row) => row.id !== selectedDimenssionId);

        //recalculeaza nr pentru toate randurile ramase
        const updatedRows = filteredRows.map((item, index) => ({
          ...item, 
          nr: filteredRows.length-index
        }));
        return updatedRows;
      });
    } catch (error) {
      const msg = error.response?.data?.detail || "An error occurred.";
      setErrorMessage(msg);
      setErrorDialogOpen(true);
    } finally {
      handleCloseDialog();
    }
  };

  const columns = [
    { field: "nr", headerName: "Nr.", minWidth: 100, flex: 0.5, headerClassName: "bold-header" },
    { field: "dimenssions", headerName: "Dimension", minWidth: 600, flex: 3, headerClassName: "bold-header" },
    ...(canEdit || canDelete ? [
      {
        field: "actions",
        headerName: "Action",
        minWidth: 200,
        flex: 1,
        headerAlign: "center",
        renderCell: (params) => (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "2px",
              width: "100%",
              height: "100%",
            }}
          >
            {canEdit && (
              <IconButton
                size="small"
                color="primary"
                onClick={() => navigate(`/dimensions/edit/${params.row.id}`)}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            )}

            {canDelete && (
              <IconButton
                size="small"
                color="error"
                onClick={() => handleOpenDialog(params.row.id)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </div>
        ),

      }
    ] : [])
  ];

  return (
    <>
      <div className="dimenssions-page">
        <div className="header-container">
          <h2>DIMENSIONS</h2>
          <div className="dimenssions-actions">
            {canCreate && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                sx={{ borderRadius: "10px" }}
                onClick={() => navigate("/dimensions/create")}
              >
                Create dimension
              </Button>
            )}
          </div>
        </div>

        <Box className="dimenssions-box-subcontainer dimenssions-page dimenssions-datagrid">
          <div className="dimenssions-data-grid-container full-width-height custom-dimenssions-datagrid">
            <DataGrid
              rows={rows}
              columns={columns}
              getRowHeight={() => "auto"}
              pageSize={5}
              initialState={{
                sorting: {
                  sortModel: [{ field: "id", sort: "desc" }],
                },
                pagination: {
                  paginationModel: { pageSize: 12 },
                },
              }}
              disableRowSelectionOnClick
              sx={{
                "& .MuiDataGrid-cell": {
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  lineHeight: "1.4",
                  display: "flex",
                  alignItems: "center",
                  paddingTop: "3px",
                  paddingBottom: "3px",
                },
                "& .MuiDataGrid-columnHeaders": {
                  whiteSpace: "normal",
                },
                "& .MuiDataGrid-cell:focus": {
                  outline: "none",
                },
                "& .MuiDataGrid-cell:focus-within": {
                  outline: "none",
                },
                "& .Mui-selected:hover": {
                  backgroundColor: "transparent !important",
                },
              }}
              pageSizeOptions={[10, 12, 20, 25]}
            />
          </div>
        </Box>


        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          aria-labelledby="confirm-delete-dialog"
        >
          <DialogTitle id="confirm-delete-dialog">Delete Confirmation</DialogTitle>
          <DialogContent>
            <Typography variant="h10">
              Are you sure you want to delete this dimension? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>CLOSE</Button>
            <Button onClick={handleConfirmDelete} color="error" variant="contained">CONFIRM DELETE</Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={errorDialogOpen}
          onClose={() => setErrorDialogOpen(false)}
          aria-labelledby="error-dialog-title"
        >
          <DialogTitle id="error-dialog-title">Error</DialogTitle>
          <DialogContent>
            <Typography>{errorMessage}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setErrorDialogOpen(false)} autoFocus>
              OK
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </>
  );
}

const Dimenssions = () => {
  const storedPermissions = JSON.parse(localStorage.getItem("permissions"));
  const permissions = storedPermissions?.pages || [];

  const hasPermission = (path, checkPermission) => {
    const pagePermission = permissions.some((page) => getPathFromUrl(page.url) === path && checkPermission(page));
    return pagePermission;
  };

  const canCreate = hasPermission('/dimensions', (page) => page.can_create);
  const canEdit = hasPermission('/dimensions', (page) => page.can_edit);
  const canDelete = hasPermission('/dimensions', (page) => page.can_delete);

  return (
    <Routes>
      <Route path="/" element={<DimenssionsList canCreate={canCreate} canEdit={canEdit} canDelete={canDelete} />} />
      <Route path="create" element={canCreate ? <AddDimenssions /> : <NotAuthorized />} />
      <Route path="edit/:id" element={canEdit ? <EditDimenssions /> : <NotAuthorized />} />
    </Routes>
  );
};

export default Dimenssions;